import React from "react";
import { Table } from "react-bootstrap";
import { Link } from "react-router-dom";

import classNames from "classnames";

import { ILeague, ITeam } from "@src/types/api";

import { TEAM_LOGO_URL } from "@lib/globals";

interface StandingsProps {
    expanded?: boolean;
    data?: ILeague;
    bordered: boolean;
    showLinks?: boolean;
}

const Standings = ({
    expanded = false,
    data,
    bordered,
    showLinks = false,
}: StandingsProps): React.JSX.Element => {
    const standings = data?.standings;

    const sortedTeams = standings
        ? data?.teams?.slice().sort((a, b) => {
              return data?.standings
                  ? data?.standings[a.id as ITeam["id"]].standing -
                        data?.standings[b.id as ITeam["id"]].standing
                  : 0;
          })
        : data && data.teams
          ? data.teams.slice().sort((a, b) => {
                return a.name.localeCompare(b.name);
            })
          : [];

    const buildHeader = () => {
        return expanded ? (
            <thead>
                <tr>
                    <th className="standing">#</th>
                    <th className="logo"></th>
                    <th className="team">Team</th>
                    <th className="wins">W</th>
                    <th className="losses">L</th>
                    <th className="ties">Tie</th>
                    <th className="rdavg">RD Avg</th>
                    <th className="rsavg">RS Avg</th>
                    <th className="raavg">RA Avg</th>
                </tr>
            </thead>
        ) : (
            <thead>
                <tr>
                    <th colSpan={5}>
                        <h3>
                            <Link to={`/leagues/${data?.id}`}>
                                {data?.ageLevel} {data?.designation}{" "}
                                {data?.direction}
                            </Link>
                        </h3>
                    </th>
                </tr>
            </thead>
        );
    };

    const buildBody = () => {
        return expanded ? (
            <tbody>
                {sortedTeams?.map((t, idx) => {
                    const teamId = t.id as ITeam["id"];

                    return (
                        <tr key={`tr-${idx}`}>
                            <td className="standing">
                                {standings?.[teamId].standing}
                            </td>
                            <td className="logo">
                                {t.logo && (
                                    <img
                                        src={`${TEAM_LOGO_URL}${t.logo}`}
                                        alt={`${t.name} logo`}
                                        className="teamLogo"
                                    />
                                )}
                            </td>
                            <td className="team">
                                <Link to={`/leagues/${data?.id}/teams/${t.id}`}>
                                    {t.name}
                                </Link>
                            </td>
                            <td className="wins">{standings?.[teamId].wins}</td>
                            <td className="losses">
                                {standings?.[teamId].losses}
                            </td>
                            <td className="ties">{standings?.[teamId].ties}</td>
                            <td className="rdavg">
                                {standings?.[teamId].rdavg}
                            </td>
                            <td className="rsavg">
                                {standings?.[teamId].rsavg}
                            </td>
                            <td className="raavg">
                                {standings?.[teamId].raavg}
                            </td>
                        </tr>
                    );
                })}
            </tbody>
        ) : (
            <tbody>
                <tr>
                    <td colSpan={2}></td>
                    <td className="wins">W</td>
                    <td className="losses">L</td>
                    <td className="ties">T</td>
                </tr>
                {sortedTeams?.map((t, idx) => {
                    const teamId = t.id as ITeam["id"];
                    return (
                        <tr key={`tr-${idx}`}>
                            <td className="logo">
                                {t.logo && (
                                    <img
                                        src={`${TEAM_LOGO_URL}${t.logo}`}
                                        alt={`${t.name} logo`}
                                        className="teamLogo"
                                    />
                                )}
                            </td>
                            <td className="team">
                                <Link to={`/leagues/${data?.id}/teams/${t.id}`}>
                                    {t.name}
                                </Link>
                            </td>
                            <td className="wins">{standings?.[teamId].wins}</td>
                            <td className="losses">
                                {standings?.[teamId].losses}
                            </td>
                            <td className="ties">{standings?.[teamId].ties}</td>
                        </tr>
                    );
                })}
            </tbody>
        );
    };

    const buildFooter = () => {
        return (
            showLinks && (
                <tfoot>
                    <tr>
                        <td colSpan={5}>
                            <div>
                                <Link
                                    to={`/leagues/${data?.id}`}
                                    className={classNames("btn btn-primary")}
                                >
                                    STANDINGS
                                </Link>

                                <Link
                                    to={`/leagues/${data?.id}/schedule`}
                                    className={classNames("btn btn-primary")}
                                >
                                    SCHEDULE
                                </Link>

                                <Link
                                    to={`/leagues/${data?.id}/playoffs`}
                                    className={classNames("btn btn-primary")}
                                >
                                    PLAYOFFS
                                </Link>
                            </div>
                        </td>
                    </tr>
                </tfoot>
            )
        );
    };

    return (
        <Table
            className={`leagueStandings ${expanded ? "expanded" : ""}`}
            striped
            bordered={bordered}
            size="sm"
            responsive
        >
            {buildHeader()}
            {buildBody()}
            {buildFooter()}
        </Table>
    );
};

export default Standings;
